import type { PendingDisposeRow } from './types';

const escapeHtml = (value: string): string =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const formatPendingTypeChange = (row: PendingDisposeRow): string => {
  if (row.pendingTypeChangeQuantity <= 0) {
    return 'None';
  }

  if (row.pendingTypeChangeMixed) {
    return `${row.pendingTypeChangeQuantity} item(s) pending • Mixed targets`;
  }

  const targetName = row.pendingTypeChangeTargetName ?? 'Selected catalog';
  return `${row.pendingTypeChangeQuantity} item(s) pending → ${targetName}`;
};

const abbreviateRecoverable = (value: string): string => {
  const normalized = value.trim().toUpperCase();
  if (normalized === 'NON-RECOVERABLE') return 'N';
  if (normalized === 'RECOVERABLE') return 'R';
  return value;
};

const buildPendingBinQueuePrintHtml = (options: {
  rows: PendingDisposeRow[];
  generatedAt: Date;
  filters?: {
    assetName?: string;
    linNsn?: string;
    fromDate?: string;
    toDate?: string;
    pendingChangesOnly?: boolean;
  };
}): string => {
  const generatedLabel = new Intl.DateTimeFormat('en-US', {
    dateStyle: 'long',
    timeStyle: 'short',
  }).format(options.generatedAt);

  const totalItems = options.rows.reduce((sum, row) => sum + row.availableCount, 0);
  const pendingTypeChangeItems = options.rows.reduce(
    (sum, row) => sum + row.pendingTypeChangeQuantity,
    0,
  );

  const filtersSummary = [];
  if (options.filters?.assetName?.trim()) {
    filtersSummary.push(`Asset Name contains "${escapeHtml(options.filters.assetName.trim())}"`);
  }
  if (options.filters?.linNsn?.trim()) {
    filtersSummary.push(`LIN/NSN contains "${escapeHtml(options.filters.linNsn.trim())}"`);
  }
  if (options.filters?.fromDate) {
    filtersSummary.push(`From ${escapeHtml(options.filters.fromDate)}`);
  }
  if (options.filters?.toDate) {
    filtersSummary.push(`To ${escapeHtml(options.filters.toDate)}`);
  }
  if (options.filters?.pendingChangesOnly) {
    filtersSummary.push('Pending changes only');
  }

  const filtersMarkup =
    filtersSummary.length > 0
      ? `<div class="filters-summary">
          <strong>Filters:</strong> ${filtersSummary.join(', ')}
        </div>`
      : '';

  const rowsMarkup = options.rows
    .map((row) => {
      const pendingTypeChange = formatPendingTypeChange(row);
      const conditionCode = row.conditionCode ? escapeHtml(row.conditionCode) : '—';
      const recoverable = escapeHtml(row.recoverable);
      const nomenclature = row.size
        ? `${escapeHtml(row.assetName)}, ${escapeHtml(row.size)}`
        : escapeHtml(row.assetName);

      return `<tr>
        <td>${escapeHtml(row.lin)}</td>
        <td class="nowrap">${escapeHtml(row.nsn)}</td>
        <td>${nomenclature}</td>
        <td class="numeric">${row.availableCount}</td>
        <td>${escapeHtml(pendingTypeChange)}</td>
        <td class="center">${conditionCode}</td>
        <td>${abbreviateRecoverable(recoverable)}</td>
      </tr>`;
    })
    .join('');

  return `<!DOCTYPE html>
<html lang="en">
  <head>
    <meta charset="UTF-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1.0" />
    <title>Pending Bin Queue</title>
    <style>
      :root {
        color-scheme: light;
      }
      * {
        box-sizing: border-box;
      }
      body {
        font-family: 'Roboto', 'Helvetica Neue', Arial, sans-serif;
        margin: 16px;
        color: #15263c;
        background: #ffffff;
      }
      header {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        gap: 24px;
        margin-bottom: 24px;
      }
      .brand {
        font-size: 14px;
        letter-spacing: 0.08em;
        text-transform: uppercase;
        color: #3a82f6;
        font-weight: 600;
      }
      .title {
        font-size: 28px;
        font-weight: 700;
        margin: 0;
        color: #15263c;
      }
      .generated {
        margin-top: 4px;
        font-size: 14px;
        color: #4a5c72;
      }
      .summary {
        display: flex;
        gap: 16px;
        flex-wrap: wrap;
      }
      .summary-chip {
        padding: 10px 16px;
        border-radius: 999px;
        background: #0d47a1;
        color: #ffffff;
        font-weight: 600;
        font-size: 13px;
        letter-spacing: 0.04em;
        text-transform: uppercase;
      }
      .filters-summary {
        margin: 16px 0;
        padding: 10px;
        background: #fef3c7;
        border: 1px solid #fde047;
        border-radius: 4px;
        font-size: 12px;
        color: #78350f;
      }
      table {
        width: 100%;
        border-collapse: collapse;
        margin-top: 16px;
        font-size: 12px;
        border: 1px solid #d7e3f4;
      }
      th, td {
        border: 1px solid #d7e3f4;
        padding: 8px 10px;
        text-align: left;
        vertical-align: top;
      }
      th {
        background: #e9f1ff;
        font-weight: 700;
        color: #4a5c72;
        font-size: 12px;
        letter-spacing: 0.05em;
        text-transform: uppercase;
      }
      tbody td {
        color: #1f2f45;
      }
      tbody tr:nth-child(even) td {
        background: #f9fbff;
      }
      td.numeric, th.numeric {
        text-align: right;
        white-space: nowrap;
      }
      td.nowrap, th.nowrap {
        white-space: nowrap;
      }
      td.center, th.center {
        text-align: center;
      }
      @media print {
        body {
          margin: 8mm;
        }
        @page {
          size: landscape;
          margin: 10mm;
        }
      }
    </style>
  </head>
  <body>
    <header>
      <div>
        <div class="brand">eQuip Transaction Suite</div>
        <h1 class="title">Pending Bin Queue</h1>
        <div class="generated">Generated ${escapeHtml(generatedLabel)}</div>
      </div>
      <div class="summary">
        <span class="summary-chip">Total Records: ${options.rows.length}</span>
        <span class="summary-chip">Total Items: ${totalItems}</span>
        <span class="summary-chip">Pending Type Change: ${pendingTypeChangeItems}</span>
      </div>
    </header>

    ${filtersMarkup}

    <table>
      <thead>
        <tr>
          <th>LIN</th>
          <th>NSN</th>
          <th>Nomenclature</th>
          <th class="numeric">Qty</th>
          <th>Pending Type Change</th>
          <th class="center">Condition Code</th>
          <th>Recoverable Type</th>
        </tr>
      </thead>
      <tbody>
        ${rowsMarkup}
      </tbody>
    </table>

    <script>
      window.addEventListener('load', () => {
        window.focus();
        window.print();
      });
    </script>
  </body>
</html>`;
};

export const printPendingBinQueue = (options: {
  rows: PendingDisposeRow[];
  filters?: {
    assetName?: string;
    linNsn?: string;
    fromDate?: string;
    toDate?: string;
    pendingChangesOnly?: boolean;
  };
}): void => {
  console.log('printPendingBinQueue called with rows:', options.rows.length);

  const printWindow = window.open('', '_blank', 'width=1200,height=800');
  if (!printWindow) {
    console.error('Failed to open print window - likely blocked by browser');
    throw new Error('Unable to open print window. Please allow pop-ups and try again.');
  }

  console.log('Print window opened successfully');

  const html = buildPendingBinQueuePrintHtml({
    rows: options.rows,
    generatedAt: new Date(),
    filters: options.filters,
  });

  console.log('HTML generated, length:', html.length);

  printWindow.document.write(html);
  printWindow.document.close();
};
